import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Stack, TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import {
  BioentityLinkFieldWithSchemaInterface,
  BioentityLinkFieldWithTagSchema,
  isRequiredLinkSchemaInterface,
  isRequiredLinkTagSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import useSchemaSSIsByIdQuery from 'schemas/util/useSchemaSSIsByIdQuery';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {Optional} from 'util/Types';

const MAX_NAME_LENGTH = 255;

const SELECT_NO_FIELD_DUMMY_OBJECT = {
  displayName: 'None',
};

// This is not an available option in the dropdown
export const UNSELECTED_DUMMY_OBJECT = {
  displayName: 'Select an option',
};

export type TranslationLinkOrDummy =
  | SchemaField
  | typeof SELECT_NO_FIELD_DUMMY_OBJECT
  | typeof UNSELECTED_DUMMY_OBJECT;

export function isSelectedTranslationLink(
  translationLinkOrDummy: Optional<TranslationLinkOrDummy>
): translationLinkOrDummy is BioentityLinkFieldWithTagSchema {
  return (
    translationLinkOrDummy != null &&
    translationLinkOrDummy !== SELECT_NO_FIELD_DUMMY_OBJECT &&
    translationLinkOrDummy !== UNSELECTED_DUMMY_OBJECT
  );
}

function isLinkedTagSchemaTranslationField(
  translationLinkOrDummy?: TranslationLinkOrDummy
): translationLinkOrDummy is BioentityLinkFieldWithTagSchema {
  return (
    isSelectedTranslationLink(translationLinkOrDummy) &&
    isRequiredLinkTagSchema(translationLinkOrDummy.requiredLink)
  );
}

function isLinkedSchemaInterfaceTranslationField(
  translationLinkOrDummy: TranslationLinkOrDummy
): translationLinkOrDummy is BioentityLinkFieldWithSchemaInterface {
  return (
    isSelectedTranslationLink(translationLinkOrDummy) &&
    isRequiredLinkSchemaInterface(translationLinkOrDummy.requiredLink)
  );
}

/**
 * Whether the given schema is compatible with the schema or interface required by the translation link field.
 */
function isSchemaValidForTranslationLink(
  schemaId: string,
  translationLinkField: TranslationLinkField,
  interfaceIdsBySchemaId: Record<string, Array<string>>
): boolean {
  const requiredLink = translationLinkField.requiredLink;
  if (isRequiredLinkTagSchema(requiredLink) && requiredLink.tagSchema.id === schemaId) {
    return true;
  }
  if (isRequiredLinkSchemaInterface(requiredLink)) {
    const schemaInterfaces = interfaceIdsBySchemaId[schemaId] ?? [];
    return schemaInterfaces.includes(requiredLink.schemaInterface.id);
  }
  return false;
}

/**
 * Whether there are any valid translation link options for the given schema.
 * If no schema is currently selected, the user can select any translation link field first,
 * then select a compatible schema after.
 */
function hasCompatibleTranslationLinkOptions(
  translationLinkFields: TranslationLinkFields,
  tagSchemaId: string | null,
  interfaceIdsBySchemaId?: Record<string, Array<string>>
): boolean {
  if (translationLinkFields.length === 0) {
    return false;
  }
  if (tagSchemaId == null) {
    return true;
  }
  assert(interfaceIdsBySchemaId != null, 'Interface IDs by schema ID is required');
  return translationLinkFields.some((translationLinkField) =>
    isSchemaValidForTranslationLink(tagSchemaId, translationLinkField.field, interfaceIdsBySchemaId)
  );
}

export type TranslationLinkField = BioentityLinkFieldWithTagSchema | BioentityLinkFieldWithSchemaInterface;

export type TranslationLinkFields = Array<{
  field: TranslationLinkField;
  isSingleLinkFieldWithExistingValue: boolean;
}>;

interface BaseCreateProteinFormProps {
  // State values (controlled)
  name: string;
  folderId: Optional<string>;
  tagSchemaId: string | null;
  selectedStudyItems: Array<StudySearchItem>;

  // Change handlers
  onNameChange: (name: string) => void;
  onFolderIdChange: (folderId: string) => void;
  onTagSchemaIdChange: (tagSchemaId: string | null) => void;
  onSelectedStudyItemsChange: (studyItems: Array<StudySearchItem>) => void;
  onTranslationLinkChange: (translationLink: TranslationLinkOrDummy) => void;
}

export type CreateProteinFormProps = BaseCreateProteinFormProps &
  (
    | {
        translationLink: TranslationLinkOrDummy;
        translationLinkFields: TranslationLinkFields;
      }
    | {
        translationLink: undefined;
        translationLinkFields: undefined;
      }
  );

export function getCreateFormValidation({
  name,
  folderId,
  translationLink,
  translationLinkFields,
  tagSchemaId,
  interfaceIdsBySchemaId,
}: {
  name: string;
  folderId: Optional<string>;
  translationLink?: TranslationLinkOrDummy;
  translationLinkFields?: TranslationLinkFields;
  tagSchemaId: string | null;
  interfaceIdsBySchemaId?: Record<string, Array<string>>;
}): {isValid: boolean; nameIsTooLong: boolean; nameErrorMessage?: string} {
  const nameOrFolderMissing = !folderId || name.trim().length === 0;
  const nameIsTooLong = name.length > MAX_NAME_LENGTH;
  const additionalSubmitDisabled =
    translationLink != null &&
    ((hasCompatibleTranslationLinkOptions(translationLinkFields ?? [], tagSchemaId, interfaceIdsBySchemaId) &&
      translationLink === UNSELECTED_DUMMY_OBJECT) ||
      (isLinkedSchemaInterfaceTranslationField(translationLink) && tagSchemaId == null));

  const nameErrorMessage = nameIsTooLong
    ? `Names can be at most 255 characters long (currently ${name.length})`
    : undefined;

  return {
    isValid: !nameOrFolderMissing && !additionalSubmitDisabled && !nameIsTooLong,
    nameIsTooLong,
    nameErrorMessage,
  };
}

export default function CreateProteinForm({
  name,
  folderId,
  tagSchemaId,
  translationLink,
  selectedStudyItems,
  onNameChange,
  onFolderIdChange,
  onTagSchemaIdChange,
  onTranslationLinkChange,
  onSelectedStudyItemsChange,
  translationLinkFields = [],
}: CreateProteinFormProps): JSX.Element {
  const {interfaceIdsBySchemaId} = useSchemaSSIsByIdQuery();

  const {nameErrorMessage} = getCreateFormValidation({
    name,
    folderId,
    translationLink,
    translationLinkFields,
    tagSchemaId,
    interfaceIdsBySchemaId,
  });

  const handleSetFolderId = (e: {target: {value: string}}): void => {
    onFolderIdChange(e.target.value);
  };

  const handleChangeTranslationLink = (field: TranslationLinkOrDummy): void => {
    onTranslationLinkChange(field);
    if (isLinkedTagSchemaTranslationField(field)) {
      // Translation links always link to a specific schema or SSI.
      onTagSchemaIdChange(field.requiredLink.tagSchema!.id);
    }
  };

  const getTranslationLinkOptions = (): Array<{
    key: number | string;
    name: string;
    handleOnClick: () => void;
    disabled?: boolean;
  }> => [
    {
      key: 'None',
      name: 'None',
      handleOnClick: () => {
        handleChangeTranslationLink(SELECT_NO_FIELD_DUMMY_OBJECT);
      },
    },
    ...translationLinkFields
      .filter(
        (translationLinkField) =>
          tagSchemaId == null ||
          isSchemaValidForTranslationLink(tagSchemaId, translationLinkField.field, interfaceIdsBySchemaId)
      )
      .map(({field, isSingleLinkFieldWithExistingValue}) => {
        if (isSingleLinkFieldWithExistingValue) {
          return {
            key: field.id,
            disabled: true,
            name: `${field.displayName} (Already specified)`,
            handleOnClick: noop,
          };
        } else {
          return {
            key: field.id,
            name: field.displayName,
            handleOnClick: () => {
              handleChangeTranslationLink(field);
            },
          };
        }
      }),
  ];

  return (
    <Stack spacing="0">
      <FormGroup>
        <FieldLabel label="Entity name" isRequired={true} htmlFor="name" />
        <TextInput
          id="name"
          autoFocus={true}
          placeholder="Name"
          onChange={(e) => {
            onNameChange(e.target.value);
          }}
          value={name}
          error={nameErrorMessage}
          onFocus={(e) => {
            const input = e.target;
            input.setSelectionRange(0, input.value.length);
          }}
          {...getTestProps({component: 'CreateProteinForm', element: 'nameInput'})}
        />
      </FormGroup>

      <div className={css(styles.row)}>
        <div className={css(styles.item)}>
          <FormGroup>
            <div>
              <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
              <FolderSelector
                buttonSize={ButtonSize.MEDIUM}
                enableRedesign={true}
                showInventory={true}
                value={folderId}
                onChange={handleSetFolderId}
                unsetOnFetchFail={true}
                allowNone={false}
              />
            </div>
          </FormGroup>
        </div>
        {BenchlingConfig.STUDIES_ENABLE && (
          <div className={css(styles.item)}>
            <FormGroup>
              <div>
                <FieldLabel label="Studies" />
                <StudySearchInput
                  selectedItems={selectedStudyItems}
                  shouldAllowMultiple={true}
                  onChange={onSelectedStudyItemsChange}
                />
              </div>
            </FormGroup>
          </div>
        )}
      </div>

      {translationLinkFields.length > 0 && (
        <FormGroup>
          <FieldLabel label="Translation link" />
          <SelectDropdown
            disabled={
              !hasCompatibleTranslationLinkOptions(translationLinkFields, tagSchemaId, interfaceIdsBySchemaId)
            }
            displayName={
              hasCompatibleTranslationLinkOptions(translationLinkFields, tagSchemaId, interfaceIdsBySchemaId)
                ? // Typescript doesn't know that translationLink must be defined if translationLinkFields is defined
                  translationLink!.displayName
                : 'No compatible fields available'
            }
            menu={
              <Menu width={MenuWidth.FULL}>
                {getTranslationLinkOptions().map(({key, name: optionName, handleOnClick, disabled}) => (
                  <Option key={key} isDisabled={disabled} onSelect={handleOnClick}>
                    {optionName}
                  </Option>
                ))}
              </Menu>
            }
            dataTestAttrs={{component: 'CreateProteinForm', element: 'translationLinkDropdown'}}
          />
        </FormGroup>
      )}
      {isFeatureEnabled({feature: RegistryFeature.REGISTRY_SET_SCHEMA}) && (
        <FormGroup>
          <FieldLabel label="Schema" />
          <TagSchemaSelector
            allowNone={!isSelectedTranslationLink(translationLink)}
            isDisabled={isLinkedTagSchemaTranslationField(translationLink)}
            oligoAwareFolderItemTypes={[ModelTypeConstants.PROTEIN]}
            selectedTagSchemaId={tagSchemaId}
            tagSchemaFilterFn={(schema) => {
              if (!isSelectedTranslationLink(translationLink)) {
                return true;
              }
              return isSchemaValidForTranslationLink(schema.id, translationLink, interfaceIdsBySchemaId);
            }}
            onChange={(tagSchema) => {
              onTagSchemaIdChange(tagSchema ? tagSchema.id : null);
            }}
          />
        </FormGroup>
      )}
    </Stack>
  );
}

const styles = StyleSheet.create({
  row: {
    display: 'flex',
    columnGap: '16px',
  },
  item: {
    flex: 1,
  },
});
